import React, { useState, useEffect, useRef } from "react";
import { Toast } from "primereact/toast";
import { Fieldset } from "primereact/fieldset";
import { Button } from "primereact/button";
import { useFormik } from "formik";
import { classNames } from "primereact/utils";
import * as Yup from "yup";
import { InputText } from "primereact/inputtext";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { useNavigate } from "react-router-dom";
import {
  obtenerTiposIdentificacion,
  obtenerTiposSolicitud,
  setTipoIdentificacion,
} from "../../sliceRedux/Reserva.slice";
import useFullPageLoader from "react-comun-seps/lib/components/FullPageLoaderHooks/useFullPageLoader";
import { useDispatch, useSelector } from "react-redux";
import RenderToast from "../../componentes/RenderToast";
import { Dropdown } from "primereact/dropdown";
import {
  appConstantes,
  nemonicoIdentificacion,
  nemonicoProceso,
  nemonicoTipoSolicitud,
} from "../../constantes/appConstantes";
import { SelectButton } from "primereact/selectbutton";
import {
  setDatos,
  setValidarDatos,
  validarDatosReserva,
  validarRuc,
  setMessageError,
  obtenerTiposEntidad,
  setTiposEntidad,
} from "../../sliceRedux/ValidarReserva.slice";
import { Message } from "primereact/message";
import { InputNumber } from "primereact/inputnumber";

const ValidarReserva = () => {
  const toast = useRef(null);
  const dispatch = useDispatch();
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [validate, setValidate] = useState(1);
  const navigate = useNavigate();
  const messageError = useSelector((state) => state.validar.messageError);
  const listaTiposIdentificacion = useSelector((state) => state.reserva.listaTiposIdentificacion);
  const listaTipoSolicitud = useSelector((state) => state.reserva.listaTipoSolicitud);
  const listaTiposEntidad = useSelector((state) => state.validar.listaTiposEntidad);
  const datosRUC = useSelector((state) => state.validar.datos);
  const validarDatos = useSelector((state) => state.validar.validarDatos);
  const [tooltipText, setTooltipText] = useState("");
  useEffect(() => {
    showLoader();
    Promise.all([dispatch(obtenerTiposIdentificacion()), dispatch(obtenerTiposSolicitud())]).then(
      () => {
        hideLoader();
      }
    );
  }, []);

  const validationSchema = Yup.object().shape({
    codigoSeps: Yup.string().required("Campo requerido."),
    identificacion: Yup.string().required("Campo requerido."),
    tipoIdentificacion: Yup.string().required("Campo requerido."),
    tipoEntidad: Yup.number().required("Campo requerido.").nullable(),
    valorActivos: Yup.number()
      .required("Campo requerido.")
      .nullable()
      .test(
        "range",
        "El valor de activos no se encuentra dentro del rango permitido.",
        function (value) {
          const { tipoEntidad } = this.parent;
          if (!tipoEntidad || value === null || value === undefined) {
            return true;
          }
          const tipoRango = listaTiposEntidad?.object?.find(
            (item) => item.codGrupoConstAdec === tipoEntidad
          );
          if (!tipoRango) return true;
          return value >= tipoRango.valDesde && value <= tipoRango.valHasta;
        }
      ),
    tipo: Yup.string().required("Campo requerido.").typeError("Campo requerido."),
    ruc: Yup.string().when("tipo", {
      is: nemonicoTipoSolicitud.ADECUACION,
      then: (schema) => schema.required("Campo requerido."),
    }),
    razonSocial: Yup.string().when("tipo", {
      is: nemonicoTipoSolicitud.ADECUACION,
      then: (schema) => schema.required("Campo requerido."),
    }),
  });

  const formik = useFormik({
    initialValues: {
      codigoSeps: "",
      tipoIdentificacion: "",
      tipoEntidad: null,
      identificacion: "",
      idTipoIdentificacion: 0,
      ruc: "",
      razonSocial: "",
      tipo: "",
      valorActivos: null,
      tipoSolicitud: "",
      idTipoSolicitud: 0,
    },
    validationSchema: validationSchema,
    onSubmit: (data) => {
      showLoader();
      let dataSend = {
        nemProceso: nemonicoProceso.SICCA_SOL_RESERV,
        numIdentificacion: data.identificacion,
        idTipoIdentificacion: data.idTipoIdentificacion,
        numSolicitud: data.codigoSeps,
        codTipoSolicitud: data.idTipoSolicitud,
        codGrupoConstAdec: data.tipoEntidad,
      };
      dispatch(validarDatosReserva(dataSend));
    },
  });

  useEffect(() => {
    if (messageError != "") {
      RenderToast(toast, "error", "Error", messageError);
      hideLoader();
    }
    return () => {
      dispatch(setMessageError(""));
    };
  }, [messageError]);

  const getFormErrorMessage = (name) => {
    return isFormFieldValid(name) && <small className="p-error">{formik.errors[name]}</small>;
  };

  const isFormFieldValid = (name) => {
    const isValid = !!(formik.touched[name] && formik.errors[name]);
    return isValid;
  };

  const handleCaptchaVerify = () => {
    setValidate(2);
  };

  useEffect(() => {
    if (listaTiposIdentificacion.code === 200) {
      hideLoader();
    }
    return () => {
      dispatch(setTipoIdentificacion({ ...listaTiposIdentificacion, ...{ code: 0 } }));
    };
  }, [listaTiposIdentificacion.code]);

  useEffect(() => {
    if (listaTiposEntidad.code === 200) {
      hideLoader();
    }
    return () => {
      dispatch(setTiposEntidad({ ...listaTiposEntidad, ...{ code: 0 } }));
    };
  }, [listaTiposEntidad.code]);

  useEffect(() => {
    if (datosRUC.nombreCompleto) {
      formik.setFieldValue("razonSocial", datosRUC?.nombreCompleto ?? "");
    }
    if (datosRUC.respuesta) {
      RenderToast(toast, "error", "Información", datosRUC?.respuesta ?? "");
    }
    return () => {
      hideLoader();
      dispatch(setDatos({ ...datosRUC, ...{ nemonicoTipoIdentificacion: 0 } }));
    };
  }, [datosRUC.nemonicoTipoIdentificacion]);

  useEffect(() => {
    if (validarDatos.code === 200) {
      sessionStorage.setItem("userData", JSON.stringify({ autenticate: true }));
      navigate("/solicitudes-constitucion", {
        state: {
          datos: { ...formik.values, redirectTo: "/constitucion" },
          codSolReservaDenominacion: validarDatos?.object?.codSolReservaDenominacion ?? 0,
          mensaje: validarDatos?.object?.mensaje ?? null,
        },
      });
    }
    return () => {
      hideLoader();
      dispatch(setValidarDatos({ ...validarDatos, ...{ code: 0 } }));
    };
  }, [validarDatos.code]);

  const optionTemplate = (option) => {
    return (
      <div className="flex flex-column ">
        <span className="font-semibold">{option.nomGrupo} </span>
        <div>{option.mensajeInformativo}</div>
      </div>
    );
  };

  const selectedTemplate = (option, props) => {
    if (option) {
      return (
        <div className="flex flex-column ">
          <span className="font-semibold">{option.nomGrupo}</span>
        </div>
      );
    }

    return <span>{props.placeholder}</span>;
  };
  return (
    <>
      {loader}
      <Toast ref={toast} />
      <div className="card">
        <div className="text-center">
          <h5>Validación de Reserva de Denominación</h5>
        </div>

        <Fieldset legend="Datos de la reserva">
          <form
            onSubmit={formik.handleSubmit}
            className="flex flex-column align-items-center w-100"
            style={{ margin: "0 auto" }}
          >
            {formik.values.tipo && (
              <div className="flex  col-12 md:col-12 text-justify">
                <Message severity="info" text={tooltipText} />
              </div>
            )}
            <div className="flex col-12 md:col-5 ">
              <label
                htmlFor="codigoSeps"
                className="mr-2"
                style={{ minWidth: "180px", textAlign: "left" }}
                required
              >
                Número solicitud reserva:
              </label>

              <div className="p-inputgroup">
                <span
                  className="p-inputgroup-addon font-semibold flex-1"
                  style={{ minWidth: "100px", textAlign: "left" }}
                >
                  SEPS-RDSF-
                </span>
                <InputText
                  id="codigoSeps"
                  name="codigoSeps"
                  value={formik.values.codigoSeps}
                  autoComplete="off"
                  className={classNames({
                    "p-invalid": isFormFieldValid("codigoSeps"),
                  })}
                  onChange={(e) => {
                    formik.setFieldValue("codigoSeps", e.target.value.toUpperCase());
                  }}
                  style={{ width: "100%" }}
                />
              </div>
            </div>
            {getFormErrorMessage("codigoSeps")}

            <div className="flex  col-12 md:col-5 ">
              <label
                htmlFor="tipoIdentificacion"
                className="mr-2"
                style={{ minWidth: "180px", textAlign: "left" }}
                required
              >
                Tipo de identificación:
              </label>
              <Dropdown
                id="tipoIdentificacion"
                name="tipoIdentificacion"
                optionLabel="nomCatalogo"
                optionValue="nomNemonico"
                placeholder="Seleccione un tipo de documento"
                options={listaTiposIdentificacion?.object || []}
                value={formik.values.tipoIdentificacion}
                emptyMessage={appConstantes.SIN_DATOS}
                className={classNames({
                  "p-invalid": isFormFieldValid("tipoIdentificacion"),
                })}
                onChange={(e) => {
                  let tipo = listaTiposIdentificacion?.object?.find(
                    (item) => item.nomNemonico === e.value
                  );
                  formik.setFieldValue("idTipoIdentificacion", tipo?.codCatalogo ?? 0);
                  formik.handleChange(e);
                }}
                style={{ width: "100%" }}
              />
            </div>
            {getFormErrorMessage("tipoIdentificacion")}

            <div className="flex col-12 md:col-5 ">
              <label
                htmlFor="identificacion"
                className="mr-2"
                style={{ minWidth: "180px", textAlign: "left" }}
                required
              >
                Identificación:
              </label>
              <InputText
                id="identificacion"
                name="identificacion"
                value={formik.values.identificacion}
                placeholder="Digite su número de identificación"
                autoComplete="off"
                maxLength={
                  formik.values.tipoIdentificacion === nemonicoIdentificacion.CEDULA ? 10 : 20
                }
                keyfilter={
                  formik.values.tipoIdentificacion === nemonicoIdentificacion.CEDULA
                    ? "int"
                    : "alphanum"
                }
                className={classNames({
                  "p-invalid": isFormFieldValid("identificacion"),
                })}
                onChange={(e) => {
                  formik.setFieldValue("identificacion", e.target.value.toUpperCase());
                }}
                style={{ width: "100%" }}
              />
            </div>
            {getFormErrorMessage("identificacion")}

            <div className="flex col-12 md:col-5 ">
              <label
                htmlFor="tipo"
                className="mr-2"
                style={{ minWidth: "180px", textAlign: "left" }}
                required
              >
                Tipo de solicitud:
              </label>
              <SelectButton
                id="tipo"
                name="tipo"
                value={formik.values.tipo}
                optionLabel="nomCatalogo"
                optionValue="nomNemonico"
                allowEmpty={false}
                color="secondary"
                onChange={(e) => {
                  formik.setFieldValue("ruc", "");
                  formik.setFieldValue("razonSocial", "");
                  formik.setFieldValue("tipoEntidad", "");
                  let tipo = listaTipoSolicitud?.object?.find(
                    (item) => item.nomNemonico === e.value
                  );
                  let idTipo = tipo?.codCatalogo ?? 0;
                  formik.setFieldValue("idTipoSolicitud", idTipo);
                  formik.setFieldValue("tipoSolicitud", tipo?.nomCatalogo ?? "");
                  showLoader();
                  dispatch(obtenerTiposEntidad(idTipo));
                  setTooltipText(tipo?.txtDescripcion ?? "");
                  formik.handleChange(e);
                }}
                className={classNames({
                  "p-invalid": isFormFieldValid("tipo"),
                })}
                options={listaTipoSolicitud?.object || []}
              />
              {formik.values.tipo && (
                <Button
                  className="ml-2"
                  rounded
                  text
                  style={{ padding: "1" }}
                  tooltip={tooltipText}
                >
                  <i className="pi pi-info-circle px-0 text-2xl"></i>
                </Button>
              )}
            </div>
            {getFormErrorMessage("tipo")}

            <div className="flex  col-12 md:col-5 ">
              <label
                htmlFor="tipoEntidad"
                className="mr-2"
                style={{ minWidth: "180px", textAlign: "left" }}
                required
              >
                Grupo de entidad:
              </label>
              <Dropdown
                id="tipoEntidad"
                name="tipoEntidad"
                optionValue="codGrupoConstAdec"
                placeholder="Seleccione un tipo de entidad"
                options={listaTiposEntidad?.object || []}
                value={formik.values.tipoEntidad}
                itemTemplate={optionTemplate}
                valueTemplate={selectedTemplate}
                emptyMessage={appConstantes.SIN_DATOS}
                className={classNames({
                  "p-invalid": isFormFieldValid("tipoEntidad"),
                })}
                onChange={formik.handleChange}
                style={{ width: "100%" }}
              />
            </div>
            {getFormErrorMessage("tipoEntidad")}
            {formik.values.tipoEntidad && (
              <div className="flex  col-12 md:col-5 ">
                <label
                  htmlFor="tipoEntidad"
                  className="mr-2"
                  style={{ minWidth: "180px", textAlign: "left" }}
                  required
                >
                  Valor activos:
                </label>
                <InputNumber
                  id="valorActivos"
                  name="valorActivos"
                  value={formik.values.valorActivos}
                  className={classNames({
                    "p-invalid": isFormFieldValid("tipoEntidad"),
                  })}
                  mode="decimal"
                  useGrouping={false}
                  maxFractionDigits={2}
                  minFractionDigits={2}
                  min={0}
                  onValueChange={(e) => {
                    formik.setFieldValue("valorActivos", e.target.value);
                  }}
                  onBlur={(e) => {
                    let tipoRango = listaTiposEntidad?.object?.find(
                      (item) => item.codGrupoConstAdec === formik.values.tipoEntidad
                    );
                    if (
                      tipoRango &&
                      (e.target.value < tipoRango.valDesde || e.target.value > tipoRango.valHasta)
                    ) {
                      RenderToast(
                        toast,
                        "warn",
                        "Información",
                        "El valor de activos no se encuentra dentro del rango permitido."
                      );
                    }
                  }}
                  style={{ width: "100%" }}
                />
              </div>
            )}
            {getFormErrorMessage("valorActivos")}
            {formik.values.tipo === nemonicoTipoSolicitud.ADECUACION && (
              <>
                <div className="flex col-12 md:col-5 ">
                  <label
                    htmlFor="ruc"
                    className="mr-2"
                    style={{ minWidth: "180px", textAlign: "left" }}
                    required
                  >
                    RUC:
                  </label>
                  <InputText
                    id="ruc"
                    name="ruc"
                    value={formik.values.ruc}
                    placeholder="Digite su número de RUC"
                    autoComplete="off"
                    maxLength={13}
                    keyfilter="int"
                    className={classNames({
                      "p-invalid": isFormFieldValid("ruc"),
                    })}
                    onChange={(e) => {
                      formik.setFieldValue("razonSocial", "");
                      formik.setFieldValue("ruc", e.target.value.toUpperCase());
                    }}
                    onBlur={(e) => {
                      if (formik.values.ruc.length === 13) {
                        showLoader();
                        dispatch(validarRuc(formik.values.ruc));
                      } else {
                        RenderToast(
                          toast,
                          "warn",
                          "Información",
                          "El campo RUC debe tener 13 dígitos."
                        );
                      }
                    }}
                    style={{ width: "100%" }}
                  />
                </div>
                {getFormErrorMessage("ruc")}
                <div className="flex col-12 md:col-5 ">
                  <label
                    htmlFor="razonSocial"
                    className="mr-2"
                    style={{ minWidth: "180px", textAlign: "left" }}
                    required
                  >
                    Razón social:
                  </label>
                  <span className="font-normal text-justify">{formik.values.razonSocial}</span>
                </div>
                {getFormErrorMessage("razonSocial")}
              </>
            )}

            {validate === 1 && (
              <div className="flex align-items-center w-100 ">
                <HCaptcha
                  sitekey="79ff2a71-b636-4833-af19-d53fd93eff78"
                  onVerify={handleCaptchaVerify}
                />
              </div>
            )}

            <Button
              label="Validar reserva"
              icon="pi pi-arrow-circle-right"
              iconPos="right"
              type="button"
              disabled={validate === 1}
              onClick={() => formik.handleSubmit()}
              className="mt-3"
            />
          </form>
        </Fieldset>
      </div>
    </>
  );
};
export default ValidarReserva;
